import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import { NotificationContext } from '@Context/NotificationContext';
import {
  useAppSelector,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { transformDictToOption } from '@Utils/transformToOptions';
import { parseDateTime } from '@Utils/transformDateTime';
import { createdUserRequestSelector } from '@Store/selectors/userInfoSelector';
import { useRespondHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { getPassportTypeDictSelector } from '@Store/selectors/dictionarySelector';
import {
  FormApproveRejectRequest,
  HrmRequestFormResponse,
} from '@Types/api/form';
import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import ApproveList from '@Components/MainForm/components/ApproveList/ApproveList';
import { FooterActions } from '@Enum/index';
import { ReassignWindow } from '@Components/ReassignWindow';
import { EMPTY_FIELD_PLACEHOLDER } from '@Constants';

import { initialValuesForm } from '../initialValues';

interface PassportRequestApproveFormProps {
  formData: HrmRequestFormResponse;
  taskId: string;
}

const PassportRequestApproveForm: React.FC<PassportRequestApproveFormProps> = ({
  formData,
  taskId,
}) => {
  const {
    taskApprovedNotification,
    taskErrorNotification,
    taskRejectedNotification,
    errorNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const passportTypes = useAppSelector(getPassportTypeDictSelector);
  const requestedUser = useAppSelector(createdUserRequestSelector);
  const [postApproveRejectForm] = useRespondHrmRequestMutation();
  const requisitionInfo = useRequisitionInfoExistingReq(formData, 'Passport');

  const [comments, setCommentValue] = useState('');
  const [isReassignOpen, setReassignOpen] = useState(false);

  const formik = useFormik({
    initialValues: initialValuesForm,
    enableReinitialize: true,
    onSubmit: (): void => {},
  });

  useEffect(() => {
    if (formData) {
      formik.setValues({
        purpose: String(
          transformDictToOption(passportTypes, formData.leaveTypeID).label
        ),
        fromDate: parseDateTime(formData.startDate, 'DD/MM/YYYY'),
        returnDate: parseDateTime(formData.endDate, 'DD/MM/YYYY'),
        totDays: String(formData.totDays),
        remarks: formData.remarks,
      });
    }
  }, [formData, passportTypes]);

  const handleApproveReject = useCallback(
    (action: FooterActions.APPROVE | FooterActions.REJECT): void => {
      const body: FormApproveRejectRequest = {
        taskId: String(taskId),
        command: action,
        comment: comments,
      };

      if (action === FooterActions.REJECT && !comments) {
        errorNotification('Please leave a comment');
        return;
      }

      postApproveRejectForm(body)
        .unwrap()
        .then(() => {
          if (action === FooterActions.APPROVE) {
            taskApprovedNotification();
          } else {
            taskRejectedNotification();
          }

          handleBackToListRequests();
        })
        .catch(() => {
          taskErrorNotification();
        });
    },
    [taskId, handleBackToListRequests, comments]
  );

  const dataSourceRequestDetails = useMemo(
    () => [
      {
        label: 'Purpose',
        value: formik.values.purpose,
      },
      {
        label: 'Remarks / Reason',
        value: formik.values.remarks || EMPTY_FIELD_PLACEHOLDER,
      },
      {
        label: 'From Date',
        value: formik.values.fromDate,
      },
      { label: 'Return Date', value: formik.values.returnDate },
      {
        label: 'Total No. of days',
        value: formik.values.totDays,
      },
    ],
    [formik.values]
  );

  const handleFooterAction = useCallback(
    (action: FooterActions, payload?: string) => {
      if (action === FooterActions.APPROVE || action === FooterActions.REJECT) {
        handleApproveReject(action);
      }

      if (action === FooterActions.COMMENTS_CHANGE) {
        setCommentValue(payload || '');
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }

      if (action === FooterActions.REASSIGN) {
        setReassignOpen(true);
      }
    },
    [handleBackToListRequests, handleApproveReject]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={requestedUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        formType="approval"
      >
        <FormItems>
          <ApproveList
            dataSource={dataSourceRequestDetails}
            label="Request Details"
          />
        </FormItems>
      </MainForm>
      <ReassignWindow
        creatorRequestNumber={requestedUser?.empNumber}
        taskId={taskId}
        isOpen={isReassignOpen}
        onClose={() => {
          setReassignOpen(false);
        }}
        onUserSelected={() => {
          setReassignOpen(false);
          handleBackToListRequests();
        }}
      />
    </Flex>
  );
};

export default PassportRequestApproveForm;
